import * as React from "react";
import { Button, SegmentedControl, Switch, Text } from "@radix-ui/themes";
import { ExitIcon } from "@radix-ui/react-icons";
import { useAuthActions } from "@convex-dev/auth/react";
import { useQuery } from "convex-helpers/react/cache/hooks";
import { api } from "@repo/convex/convex/_generated/api";
import { toast } from "sonner";
import { getAppVersion } from "@repo/shared/app/app.ts";
import { PageHeader } from "../common/PageHeader.tsx";
import { Section } from "../common/section/Section.tsx";
import { MyProfileSection } from "./myProfile/MyProfileSection.tsx";
import { DebugSection } from "./DebugSection.tsx";
import { useMe } from "../users/meHooks.ts";
import { Appearance, appearanceLabels, useAppearance } from "../app/appearance.ts";
import { optimisticallyUpdateMySettings } from "./settingsHooks.ts";
import "./settings.css";

interface Props {
  children?: React.ReactNode;
}

export const SettingsPage: React.FC<Props> = ({}) => {
  return (
    <div className="settings">
      <PageHeader title="Settings" />
      <AccountSection />
      <AppearanceSection />
      <ArchivingSection />
      <MyProfileSection />
      <p className="settings-version">StashIt {getAppVersion()}</p>
      <details className="settings-debug">
        <summary>Debug</summary>
        <DebugSection />
      </details>
    </div>
  );
};

const AccountSection: React.FC = () => {
  const me = useMe();
  const { signOut } = useAuthActions();
  return (
    <Section title="Account">
      <div className="settings-row">
        <img className="settings-avatar" src="/logo.webp" alt="" />
        <div className="settings-row-text">
          <Text as="div" weight="bold">
            {me?.email ?? "\u00a0"}
          </Text>
          <Text as="div" size="2" color="gray">
            Signed in on this browser
          </Text>
        </div>
        <Button variant="soft" color="gray" onClick={() => void signOut()}>
          <ExitIcon /> Sign out
        </Button>
      </div>
    </Section>
  );
};

const AppearanceSection: React.FC = () => {
  const { appearance, setAppearance } = useAppearance();
  return (
    <Section
      title="Appearance"
      description="Kept on this browser. System follows your device's own setting. The reader's page has its own colours, under Aa."
    >
      <SegmentedControl.Root
        size="2"
        style={{ width: "100%" }}
        value={appearance}
        onValueChange={(value) => setAppearance(value as Appearance)}
      >
        {(Object.keys(appearanceLabels) as Appearance[]).map((value) => (
          <SegmentedControl.Item key={value} value={value}>
            {appearanceLabels[value]}
          </SegmentedControl.Item>
        ))}
      </SegmentedControl.Root>
    </Section>
  );
};

const ArchivingSection: React.FC = () => {
  const settings = useQuery(api.userSettings.getMineOrDefault);
  const updateSettings = optimisticallyUpdateMySettings();
  const learn = settings?.settings.shouldUpdateMyProfileWhenArchivingItem ?? false;
  return (
    <Section
      title="Archiving"
      description="What the switch starts as when you tuck something into the archive."
    >
      <label className="settings-row settings-switch">
        <div className="settings-row-text">
          <Text as="div" weight="bold">
            Let the AI learn from what I archive
          </Text>
          <Text as="div" size="2" color="gray">
            Uses the article and your notes to update your profile, an hour after you archive it.
          </Text>
        </div>
        <Switch
          checked={learn}
          disabled={!settings}
          onCheckedChange={(checked) =>
            updateSettings({ shouldUpdateMyProfileWhenArchivingItem: checked }).catch(toast.error)
          }
        />
      </label>
    </Section>
  );
};
